import { createId } from './id';
import { putMedia, getMedia } from './db';
import type { MediaAsset } from '@/types';

export const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
export const ACCEPTED_AUDIO_TYPES = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/x-wav', 'audio/m4a', 'audio/mp4', 'audio/aac', 'audio/ogg'];
export const ACCEPTED_WATERMARK_TYPES = ['image/png', 'image/webp', 'image/svg+xml'];

/** Longest edge we ever keep for a stored image — comfortably above the 1080x1920 export target. */
const MAX_IMAGE_EDGE_PX = 2560;
const MAX_IMAGE_FILE_BYTES = 25 * 1024 * 1024; // 25MB
const MAX_AUDIO_FILE_BYTES = 40 * 1024 * 1024; // 40MB

export class MediaValidationError extends Error {}

export function validateImageFile(file: File): void {
  if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
    throw new MediaValidationError(`Unsupported image format: ${file.type || 'unknown'}. Use JPG, PNG or WebP.`);
  }
  if (file.size > MAX_IMAGE_FILE_BYTES) {
    throw new MediaValidationError(`"${file.name}" is too large (max ${MAX_IMAGE_FILE_BYTES / (1024 * 1024)}MB).`);
  }
}

export function validateAudioFile(file: File): void {
  if (!ACCEPTED_AUDIO_TYPES.some((t) => file.type === t) && !/\.(mp3|wav|m4a|aac|ogg)$/i.test(file.name)) {
    throw new MediaValidationError(`Unsupported audio format: ${file.type || 'unknown'}. Use MP3, WAV, M4A or AAC.`);
  }
  if (file.size > MAX_AUDIO_FILE_BYTES) {
    throw new MediaValidationError(`"${file.name}" is too large (max ${MAX_AUDIO_FILE_BYTES / (1024 * 1024)}MB).`);
  }
}

export function validateWatermarkFile(file: File): void {
  if (!ACCEPTED_WATERMARK_TYPES.includes(file.type)) {
    throw new MediaValidationError(`Unsupported watermark format: ${file.type || 'unknown'}. Use PNG, WebP or SVG.`);
  }
}

/** Loads an image file, downscales it if oversized, and stores it (compressed) in IndexedDB. */
export async function storeImageFile(file: File): Promise<MediaAsset> {
  validateImageFile(file);
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, MAX_IMAGE_EDGE_PX / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);

  let blob: Blob = file;
  if (scale < 1 || file.type === 'image/png') {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D context unavailable.');
    ctx.drawImage(bitmap, 0, 0, width, height);
    const encoded = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, file.type === 'image/png' ? 'image/png' : 'image/jpeg', 0.9)
    );
    if (encoded) blob = encoded;
  }
  bitmap.close();

  const asset: MediaAsset = {
    id: createId('media'),
    kind: 'image',
    mimeType: blob.type || file.type,
    fileName: file.name,
    sizeBytes: blob.size,
    width,
    height,
    createdAt: Date.now(),
  };
  await putMedia(asset, blob);
  return asset;
}

/** Stores an audio file as-is (no re-encoding in-browser) and probes its duration. */
export async function storeAudioFile(file: File): Promise<MediaAsset> {
  validateAudioFile(file);
  const durationSeconds = await probeAudioDuration(file);
  const asset: MediaAsset = {
    id: createId('media'),
    kind: 'audio',
    mimeType: file.type || 'audio/mpeg',
    fileName: file.name,
    sizeBytes: file.size,
    durationSeconds,
    createdAt: Date.now(),
  };
  await putMedia(asset, file);
  return asset;
}

export async function storeAudioBlob(blob: Blob, fileName: string): Promise<MediaAsset> {
  const durationSeconds = await probeAudioDuration(blob);
  const asset: MediaAsset = {
    id: createId('media'),
    kind: 'audio',
    mimeType: blob.type || 'audio/wav',
    fileName,
    sizeBytes: blob.size,
    durationSeconds,
    createdAt: Date.now(),
  };
  await putMedia(asset, blob);
  return asset;
}

/** Stores a finished rendered MP4 (or other output) blob in IndexedDB for later download. */
export async function storeVideoBlob(blob: Blob, fileName: string): Promise<MediaAsset> {
  const asset: MediaAsset = {
    id: createId('media'),
    kind: 'video',
    mimeType: blob.type || 'video/mp4',
    fileName,
    sizeBytes: blob.size,
    createdAt: Date.now(),
  };
  await putMedia(asset, blob);
  return asset;
}

export function probeAudioDuration(source: Blob): Promise<number> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(source);
    const audio = new Audio();
    const cleanup = () => URL.revokeObjectURL(url);
    audio.addEventListener('loadedmetadata', () => {
      const duration = Number.isFinite(audio.duration) ? audio.duration : 0;
      cleanup();
      resolve(duration);
    });
    audio.addEventListener('error', () => {
      cleanup();
      reject(new MediaValidationError('Could not read audio file — it may be corrupted or unsupported.'));
    });
    audio.src = url;
  });
}

// ---------------------------------------------------------------------------
// Object URL lifecycle management
// ---------------------------------------------------------------------------

const urlCache = new Map<string, { url: string; refCount: number }>();

/** Returns a (possibly cached) object URL for a media id. Call releaseObjectUrl when done. */
export async function acquireObjectUrl(mediaId: string): Promise<string | null> {
  const cached = urlCache.get(mediaId);
  if (cached) {
    cached.refCount += 1;
    return cached.url;
  }
  const record = await getMedia(mediaId);
  if (!record) return null;
  const url = URL.createObjectURL(record.blob);
  urlCache.set(mediaId, { url, refCount: 1 });
  return url;
}

export function releaseObjectUrl(mediaId: string): void {
  const cached = urlCache.get(mediaId);
  if (!cached) return;
  cached.refCount -= 1;
  if (cached.refCount <= 0) {
    URL.revokeObjectURL(cached.url);
    urlCache.delete(mediaId);
  }
}

/** Revokes every cached object URL — used on full app reset. */
export function releaseAllObjectUrls(): void {
  for (const { url } of urlCache.values()) URL.revokeObjectURL(url);
  urlCache.clear();
}
